import React, { useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CartSummaryBlock } from "@/types/content";
import { useBodyClass } from "@/hooks/useBodyClass";
import { useCart } from "@/hooks/useCart";
import { useLocalizedTexts, t } from "@/utils/translations";
import { confirmPlanChange } from "@/api/planChange";
import { useMyAccountPath } from "@/contexts/MyAccountPathContext";
import {
  parseFormattedAmount,
  formatMinorUnitsAsCurrency as formatMinorUnits,
} from "@/utils/priceFormat";
import { formatBillingPeriodLabel } from "@/utils/billingPeriod";
import { LegacyMigrationNotice } from "@/components/content/LegacyMigrationNotice";
import { FreeDeliveryNotice } from "@/components/content/FreeDeliveryNotice";
import { CartCurrencyWarning } from "@/components/checkout/CurrencySwitchDialog";
import { useCartCurrencyStatus } from "@/hooks/useCartCurrencyStatus";
import { PlanChangeReceipt } from "./PlanChangeReceipt";
import {
  GrantCoverageNotice,
  GrantChargeDate,
} from "@/components/content/GrantCoverageNotice";
import styles from "./CartSummary.module.css";

/**
 * Parse a formatted cart money string (e.g. "699,00 €") to minor units, or null
 * when absent/unparseable. Cart-level prices are only exposed as pre-formatted
 * strings; normalization is shared via parseFormattedAmount (see priceFormat.ts,
 * also used by useCheckout.ts).
 */
function parseToMinor(formatted: string | null): number | null {
  const n = parseFormattedAmount(formatted);
  return n === null ? null : Math.round(n * 100);
}

/**
 * Coupon rejections that get a specific message, keyed by the backend's
 * validation translation key, with the English fallback for each.
 */
const COUPON_ERROR_FALLBACKS: Record<string, string> = {
  "Tracker.Validation.Coupon.AlreadyUsed":
    "This code has already been used on your account",
  "Tracker.Validation.Coupon.SignInRequired": "Please sign in to use this code",
};

export interface CartSummaryProps {
  /** Unused by the component (it reads cart state from context); optional so the
   *  summary can be reused outside the block renderer, e.g. the quick order page. */
  block?: CartSummaryBlock;
}

const PAYMENT_ICONS = [
  { src: "/images/partners/visa.svg", alt: "Visa" },
  { src: "/images/partners/master-card.svg", alt: "Mastercard" },
  { src: "/images/partners/master-card-maestro.svg", alt: "Maestro" },
  { src: "/images/partners/visa-electron.svg", alt: "Visa Electron" },
  { src: "/images/partners/klarna.svg", alt: "Klarna" },
];

export const CartSummary: React.FC<CartSummaryProps> = () => {
  useBodyClass("page--light");
  const {
    cart,
    isLoading,
    checkoutUrl,
    applyCoupon,
    removeCoupon,
    refreshCart,
    unresolvedVariantLineIds,
    requestVariantFocus,
  } = useCart();
  const localizedTexts = useLocalizedTexts();
  const navigate = useNavigate();
  const myAccountPath = useMyAccountPath();
  const [promoCode, setPromoCode] = useState("");
  const [promoError, setPromoError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isConfirmingPlanChange, setIsConfirmingPlanChange] = useState(false);
  const [planChangeError, setPlanChangeError] = useState<string | null>(null);

  // A bundle added from a product card lands in the cart with no version chosen
  // for its variable items. The backend refuses such a checkout outright; this
  // stops the customer walking into that refusal a step later.
  const hasUnresolvedVariants = unresolvedVariantLineIds.length > 0;
  const variantErrorId = useId();

  // Whether the member can pay for this cart at all, currency-wise. Asked here rather
  // than at the payment step so a pinned member is not sent through the whole checkout
  // before finding out. Only queried once the cart actually has something in it.
  const { status: currencyStatus, refetch: refetchCurrencyStatus } =
    useCartCurrencyStatus({ enabled: (cart?.lines?.length ?? 0) > 0 });

  const currencyWarning = (
    <CartCurrencyWarning
      status={currencyStatus}
      onSwitched={() => {
        // The cart itself is unchanged by a switch, but re-reading the status is what
        // clears the warning — and the fresh cart keeps the totals honest.
        refetchCurrencyStatus();
        refreshCart();
      }}
    />
  );

  const handleApplyCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    setPromoError(null);
    if (!promoCode.trim()) return;

    const result = await applyCoupon(promoCode.trim());
    if (result.ok) {
      setPromoCode("");
      return;
    }
    // Once-per-member codes (GROW-386) get their own message; everything else
    // keeps the generic one.
    const fallback = result.errorKey
      ? COUPON_ERROR_FALLBACKS[result.errorKey]
      : undefined;
    setPromoError(
      fallback && result.errorKey
        ? t(localizedTexts, result.errorKey, fallback)
        : t(
            localizedTexts,
            "Tracker.Cart.InvalidPromoCode",
            "Invalid promo code"
          )
    );
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <p className={styles.loading}>
          {t(
            localizedTexts,
            "Tracker.Cart.LoadingSummary",
            "Loading summary..."
          )}
        </p>
      </div>
    );
  }

  if (!cart) {
    return null;
  }

  const membershipChange = cart.membershipChange;
  const gpsMaxMigration = cart.gpsMaxMigration;
  const isPaidPlanChange = membershipChange?.isPaidUser === true;
  const proratedTotalString =
    isPaidPlanChange &&
    membershipChange?.prorationAmountMinor !== null &&
    membershipChange?.prorationAmountMinor !== undefined &&
    membershipChange?.prorationCurrency
      ? formatMinorUnits(
          membershipChange.prorationAmountMinor,
          membershipChange.prorationCurrency
        )
      : null;

  async function handleConfirmPlanChange() {
    if (isConfirmingPlanChange) return;
    setIsConfirmingPlanChange(true);
    setPlanChangeError(null);
    const result = await confirmPlanChange();
    if (result.ok) {
      await refreshCart();
      navigate(`${myAccountPath}?tab=subscriptions`);
      return;
    }
    setIsConfirmingPlanChange(false);
    setPlanChangeError(result.error);
  }

  // Show the removable "Promo <code>" row whenever a code is applied. This must
  // NOT key off a subtotal difference: order-level ("Order amount" reward)
  // discounts don't change the subtotal, so that check hid the row — and its
  // Remove button — for those discounts entirely.
  const hasDiscountCodes = cart.discountCodes.length > 0;

  // Line-level price adjustments (member-group discount, "Order line amount"
  // reward, Stripe coupon, etc.) summed from the lines. Minor units avoid
  // floating-point drift across many lines.
  const lineDiscountMinor = cart.lines.reduce((sum, line) => {
    const delta = line.totalPriceWithoutAdjustments - line.totalPrice;
    return sum + Math.round(delta * 100);
  }, 0);
  // Order-level discounts ("Order amount" reward) never touch the lines, so the
  // line sum above misses them. In the cart there's no delivery yet and
  // Total = cart.totalPrice, so an order-level discount is exactly the gap
  // between the (line-adjusted) Subtotal and the Total. Adding it covers both
  // reward types without double-counting line-level discounts (they're already
  // out of subtotalPrice). Parsing failures fall back to line-level only.
  const subtotalMinor = parseToMinor(cart.subtotalPrice);
  const totalMinor = parseToMinor(cart.totalPrice);
  const orderLevelDiscountMinor =
    subtotalMinor !== null && totalMinor !== null
      ? Math.max(0, subtotalMinor - totalMinor)
      : 0;
  const totalDiscountMinor = lineDiscountMinor + orderLevelDiscountMinor;
  // The GPS MAX migration credit is itself an adjustment; it gets its own
  // labelled row below, so it's carved out of the generic discount row to
  // avoid showing the same amount twice.
  const migrationCreditMinor =
    gpsMaxMigration && gpsMaxMigration.creditAppliedMinor > 0
      ? gpsMaxMigration.creditAppliedMinor
      : 0;
  const otherAdjustmentMinor = totalDiscountMinor - migrationCreditMinor;
  const hasAdjustment = otherAdjustmentMinor > 0 && !!cart.currency;
  const formattedAdjustment =
    hasAdjustment && cart.currency
      ? `−${formatMinorUnits(otherAdjustmentMinor, cart.currency)}`
      : null;

  const isB2b = cart.isB2b;

  const header = (
    <button
      type="button"
      className={styles.header}
      onClick={() => setIsExpanded(!isExpanded)}
      aria-expanded={isExpanded}
    >
      <span className={styles.headerText}>
        {t(
          localizedTexts,
          "Tracker.Cart.YourShoppingCart",
          "Your shopping cart"
        )}
      </span>
      <svg
        className={`${styles.chevron} ${isExpanded ? styles.chevronUp : ""}`}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <polyline points="6 8 10 12 14 8" />
      </svg>
    </button>
  );

  // A paid member changing plan gets the itemised upgrade receipt instead of the
  // generic subtotal/discount/total card: here the cart line's price is the NEW
  // plan's recurring price, not what's being charged, and showing it as a
  // "Subtotal" above a much smaller "Total" is what members were reporting as a
  // broken price. GPS MAX migration carts keep their own credit row + banner,
  // and free → paid carts keep the standard checkout card.
  const showPlanChangeReceipt =
    isPaidPlanChange &&
    !gpsMaxMigration &&
    !!membershipChange &&
    membershipChange.prorationAmountMinor !== null &&
    !!membershipChange.prorationCurrency;

  if (showPlanChangeReceipt && membershipChange) {
    return (
      <div className={styles.container} data-component="cart-summary">
        {currencyWarning}
        {header}
        {isExpanded && (
          <PlanChangeReceipt
            change={membershipChange}
            merchant={cart.merchant}
            recurringPrice={cart.lines[0]?.formattedUnitPrice ?? null}
            periodLabel={formatBillingPeriodLabel(
              cart.billingPeriod,
              localizedTexts
            )}
            isConfirming={isConfirmingPlanChange}
            hasError={planChangeError !== null}
            onConfirm={handleConfirmPlanChange}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.container} data-component="cart-summary">
      {currencyWarning}
      {header}

      {isExpanded && (
        <div className={styles.content}>
          {/* A promotional grant being honoured. Sits above the numbers because it explains
              them: either the total shown is not what is charged today (deferred), or the
              reduction in it needs a name (credited). Renders nothing on any other cart. */}
          <GrantCoverageNotice membershipChange={membershipChange} />
          {/* Price breakdown */}
          <div className={styles.breakdown}>
            <div className={styles.row}>
              <span>
                {t(localizedTexts, "Tracker.Checkout.Subtotal", "Subtotal")}
              </span>
              <span className={styles.rowValue}>
                {cart.subtotalPriceWithoutAdjustments ??
                  cart.subtotalPrice ??
                  "–"}
              </span>
            </div>

            {/* Aggregate adjustment row — surfaces any line-level discount that
                would otherwise make Subtotal + Delivery ≠ Total. Fires for ANY
                discount source (promo code, member-group price, Stripe coupon,
                etc.), not just promo codes — discountCodes-gated rows below
                cover the per-code remove affordance. */}
            {hasAdjustment && formattedAdjustment && (
              <div className={styles.row}>
                <span>
                  {t(localizedTexts, "Tracker.Cart.Discount", "Discount")}
                </span>
                <span className={styles.rowValue}>{formattedAdjustment}</span>
              </div>
            )}

            {/* GPS MAX migration credit — the unused time of the subscriptions
                being replaced, applied to the first payment only. */}
            {migrationCreditMinor > 0 && gpsMaxMigration && (
              <div className={styles.row}>
                <span>
                  {t(
                    localizedTexts,
                    "Tracker.Cart.GpsMaxMigration.CreditRow",
                    "Migration credit"
                  )}
                </span>
                <span className={styles.rowValue}>
                  −
                  {formatMinorUnits(
                    migrationCreditMinor,
                    gpsMaxMigration.currency
                  )}
                </span>
              </div>
            )}

            {/* Discount codes */}
            {hasDiscountCodes &&
              cart.discountCodes.map((code) => (
                <div key={code} className={styles.discountRow}>
                  <div className={styles.row}>
                    <span>
                      {t(localizedTexts, "Tracker.Checkout.Promo", "Promo")}{" "}
                      {code}
                    </span>
                    <span className={styles.rowValue}>
                      {/* Show the difference as a negative */}–
                    </span>
                  </div>
                  <button
                    type="button"
                    className={styles.removeDiscount}
                    onClick={() => removeCoupon(code)}
                  >
                    {t(localizedTexts, "Tracker.Generic.Remove", "Remove")}
                  </button>
                </div>
              ))}
          </div>

          {/* Promo code input — consumer-only; B2B members order by invoice and
              don't use promo codes. */}
          {!isB2b && (
            <div className={styles.promoSection}>
              <label className={styles.promoLabel} htmlFor="promo-code-input">
                {t(
                  localizedTexts,
                  "Tracker.Cart.ApplyPromoCode",
                  "Apply promo code"
                )}
              </label>
              <form className={styles.promoForm} onSubmit={handleApplyCoupon}>
                <input
                  id="promo-code-input"
                  type="text"
                  className={styles.promoInput}
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                />
                <button type="submit" className={styles.promoButton}>
                  {t(localizedTexts, "Tracker.Generic.Apply", "Apply")}
                </button>
              </form>
              {promoError && <p className={styles.promoError}>{promoError}</p>}
            </div>
          )}

          {/* GPS MAX migration banner — replaces the generic plan-change banner
              for migration carts. Sourced from order properties so it survives
              a hard refresh of the cart page. */}
          {gpsMaxMigration && (
            <div className={styles.legacyMigrationNotice} role="note">
              <p className={styles.planChangeHeading}>
                {t(
                  localizedTexts,
                  "Tracker.Cart.GpsMaxMigration.Heading",
                  "GPS MAX migration"
                )}
              </p>
              <p className={styles.legacyMigrationBody}>
                {t(
                  localizedTexts,
                  "Tracker.Cart.GpsMaxMigration.Body",
                  "When you complete this purchase, your current subscriptions ({count}) will be cancelled. Unused time is applied as the credit above."
                ).replace(
                  "{count}",
                  String(gpsMaxMigration.subscriptionsToCancelCount)
                )}
              </p>
            </div>
          )}

          {/* Membership plan change banner */}
          {membershipChange && !gpsMaxMigration && (
            <div className={styles.planChangeNotice}>
              <p className={styles.planChangeHeading}>
                {t(
                  localizedTexts,
                  "Tracker.Cart.PlanChange.Heading",
                  "Plan change"
                )}
              </p>
              <p className={styles.planChangeBody}>
                {membershipChange.currentTierIdentifier
                  ? `${membershipChange.currentTierIdentifier} → ${membershipChange.newTierIdentifier}`
                  : membershipChange.newTierIdentifier}
              </p>
              {isPaidPlanChange && proratedTotalString && (
                <p className={styles.planChangeProration}>
                  {t(
                    localizedTexts,
                    "Tracker.Cart.PlanChange.DueToday",
                    "Due today"
                  )}
                  : {proratedTotalString}
                </p>
              )}
            </div>
          )}

          {/* Legacy Stripe account migration: full price here, cash refund after
              checkout. The same notice is repeated at the payment step — see
              LegacyMigrationNotice. */}
          <LegacyMigrationNotice membershipChange={membershipChange} />

          {/* Total */}
          <div className={styles.totalSection}>
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>
                {t(localizedTexts, "Tracker.Checkout.Total", "Total")}
              </span>
              <span className={styles.totalValue}>
                {proratedTotalString ?? cart.totalPrice ?? "–"}
              </span>
            </div>
            {/* Repeats the notice above on purpose. The total is the one number that reads as
                "due now", so when a promotional grant defers the charge, the date has to sit
                with the amount — not only in a banner the member may have skimmed past. */}
            <GrantChargeDate membershipChange={membershipChange} />
            <p className={styles.totalDisclaimer}>
              {t(
                localizedTexts,
                "Tracker.Cart.ShippingDisclaimer",
                "Additional fees & taxes may apply. Enter your shipping information at the next stage to see shipping costs."
              )}
            </p>
          </div>

          {/* Checkout button — paid plan changes call Subscription.Update via our endpoint;
              free users and non-membership carts go through the standard checkout link. */}
          <div className={styles.checkoutSection}>
            {/* How close a retailer's order is to free delivery — last thing before they commit,
                which is the last moment adding another item is still an option. Lives here rather
                than at checkout for the same reason, and reaches the quick order tab for free,
                since that page renders this same summary. Inside this section, not between the
                two, so the total's hairline above it stays visible and the card keeps its
                section rhythm. B2B carts only; renders nothing else. */}
            <FreeDeliveryNotice cart={cart} />
            {isPaidPlanChange ? (
              <>
                <button
                  type="button"
                  className={styles.checkoutButton}
                  onClick={handleConfirmPlanChange}
                  disabled={isConfirmingPlanChange}
                  aria-busy={isConfirmingPlanChange ? "true" : undefined}
                >
                  {isConfirmingPlanChange
                    ? t(
                        localizedTexts,
                        "Tracker.Cart.PlanChange.Confirming",
                        "Confirming…"
                      )
                    : t(
                        localizedTexts,
                        "Tracker.Cart.PlanChange.ConfirmButton",
                        "Confirm plan change"
                      )}
                </button>
                {planChangeError && (
                  <p className={styles.planChangeError} role="alert">
                    {t(
                      localizedTexts,
                      "Tracker.Cart.PlanChange.Error",
                      "Couldn't change your plan. Please try again or update your payment method."
                    )}
                  </p>
                )}
              </>
            ) : (
              checkoutUrl && (
                <>
                  {/* Still an anchor so middle-click and the status bar behave
                      normally; the guard is on the click. A cart line that never
                      had a version chosen would otherwise reach fulfilment
                      without saying which size or colour to ship. */}
                  <a
                    href={checkoutUrl}
                    className={styles.checkoutButton}
                    aria-disabled={hasUnresolvedVariants || undefined}
                    aria-describedby={
                      hasUnresolvedVariants ? variantErrorId : undefined
                    }
                    onClick={(event) => {
                      if (!hasUnresolvedVariants) return;
                      event.preventDefault();
                      // Points at the offending row rather than leaving the
                      // customer to hunt for it; the row is in a sibling block,
                      // so the ask goes through the cart context.
                      requestVariantFocus();
                    }}
                  >
                    {t(
                      localizedTexts,
                      "Tracker.Cart.ContinueToCheckout",
                      "Continue to checkout"
                    )}
                  </a>
                  {hasUnresolvedVariants && (
                    <p
                      className={styles.planChangeError}
                      id={variantErrorId}
                      role="alert"
                    >
                      {t(
                        localizedTexts,
                        "Tracker.Checkout.VariantSelectionRequired",
                        "Choose a version for every item before continuing."
                      )}
                    </p>
                  )}
                </>
              )
            )}
          </div>

          {/* Payment methods — consumer-only; B2B members pay by invoice, so the
              consumer card/Klarna icons aren't relevant. */}
          {!isB2b && (
            <div className={styles.paymentSection}>
              <p className={styles.paymentLabel}>
                {t(
                  localizedTexts,
                  "Tracker.Cart.AcceptedPaymentMethods",
                  "Accepted payment methods:"
                )}
              </p>
              <div className={styles.paymentIcons}>
                {PAYMENT_ICONS.map((icon) => (
                  <img
                    key={icon.alt}
                    src={icon.src}
                    alt={icon.alt}
                    className={styles.paymentIcon}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CartSummary;
